import type { LayerSpecification, StyleSpecification } from "maplibre-gl";
import { normalizeColor } from "./colors";
import { AnyLayer, layerById, readPath, rewrite } from "./paths";
import type { SimpleComponent, SimpleModel, Slot } from "./types";

export function getSlotColor(
  style: StyleSpecification,
  slot: Slot
): string | undefined {
  const first = slot.paths[0];
  const value = readPath(layerById(style, first.layerId), first);
  return typeof value === "string" ? value : undefined;
}

// True while every slot still holds one single colour. False means the style
// was edited elsewhere (Advanced, undo, a different style) and the model must
// be rebuilt.
export function isModelConsistent(
  style: StyleSpecification,
  model: SimpleModel
): boolean {
  const layers = new Map(
    (style.layers || []).map((layer) => [layer.id, layer as AnyLayer])
  );
  return model.slots.every((slot) => {
    const colors = slot.paths.map((target) => {
      const value = readPath(layers.get(target.layerId), target);
      return typeof value === "string" ? normalizeColor(value) : undefined;
    });
    return colors[0] !== undefined && colors.every((c) => c === colors[0]);
  });
}

// Returns a new style with the slot's colour replaced everywhere it occurs.
// Places that no longer hold a colour are left alone.
export function setSlotColor<S extends StyleSpecification>(
  style: S,
  slot: Slot,
  color: string
): S {
  const next = normalizeColor(color);
  return rewrite(style, slot.paths, () => next);
}

export function isComponentVisible(
  style: StyleSpecification,
  component: SimpleComponent
): boolean {
  return component.layerIds.some(
    (id) => layerById(style, id)?.layout?.visibility !== "none"
  );
}

export function setComponentVisible<S extends StyleSpecification>(
  style: S,
  component: SimpleComponent,
  visible: boolean
): S {
  const ids = new Set(component.layerIds);
  const layers = style.layers.map((layer) => {
    if (!ids.has(layer.id)) return layer;
    const layout = { ...((layer as AnyLayer).layout || {}) };
    layout.visibility = visible ? "visible" : "none";
    return { ...layer, layout } as LayerSpecification;
  });
  return { ...style, layers };
}

const DEFAULT_TEXT_SIZE = 16;

function scaleNumber(value: number, factor: number): number {
  return Math.max(1, Math.round(value * factor * 10) / 10);
}

// Scales one output of a text-size expression. A nested expression is wrapped
// in ["*", factor, expr]; an existing wrapper has its factor updated instead,
// so repeated scaling does not nest.
function scaleOutput(value: unknown, factor: number): unknown {
  if (typeof value === "number") return scaleNumber(value, factor);
  if (!Array.isArray(value)) return undefined;
  if (value[0] === "*" && value.length === 3 && typeof value[1] === "number") {
    return ["*", Math.round(value[1] * factor * 100) / 100, value[2]];
  }
  return ["*", factor, value];
}

// Scales a text-size value. Returns undefined when the value has a form that
// cannot be scaled safely, so the caller leaves it alone.
export function scaleTextSize(value: unknown, factor: number): unknown {
  if (value === undefined) return scaleNumber(DEFAULT_TEXT_SIZE, factor);
  if (typeof value === "number") return scaleNumber(value, factor);

  if (Array.isArray(value)) {
    const op = value[0];
    const isInterpolate = typeof op === "string" && op.startsWith("interpolate");
    if (!isInterpolate && op !== "step") return undefined;
    // Output positions: interpolate 4,6,8…; step 2,4,6…
    const first = isInterpolate ? 4 : 2;
    const scaled = value.slice();
    for (let i = first; i < value.length; i += 2) {
      const output = scaleOutput(value[i], factor);
      if (output === undefined) return undefined;
      scaled[i] = output;
    }
    return scaled;
  }

  const stops = (value as { stops?: unknown })?.stops;
  if (Array.isArray(stops)) {
    if (!stops.every((s) => Array.isArray(s) && typeof s[1] === "number")) {
      return undefined;
    }
    return {
      ...(value as object),
      stops: stops.map((s) => [s[0], scaleNumber(s[1], factor)]),
    };
  }
  return undefined;
}

export function scaleLabelSize<S extends StyleSpecification>(
  style: S,
  component: SimpleComponent,
  factor: number
): S {
  const ids = new Set(component.layerIds);
  const layers = style.layers.map((layer) => {
    if (!ids.has(layer.id) || layer.type !== "symbol") return layer;
    const layout = { ...((layer as AnyLayer).layout || {}) };
    const scaled = scaleTextSize(layout["text-size"], factor);
    if (scaled === undefined) return layer;
    layout["text-size"] = scaled;
    return { ...layer, layout } as LayerSpecification;
  });
  return { ...style, layers };
}
